import React, { useState, useEffect } from 'react';
import "../../assets/css/Driver.css";
import { FaTruck, FaChevronRight, FaSortAmountUpAlt, FaInfoCircle, FaTrashAlt, FaPlus, FaSearch, FaEuroSign, FaTimes, FaCheck, FaMapMarkerAlt, FaCalendarAlt, FaWeightHanging, FaRoad, FaArrowLeft } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { Link, useParams, useNavigate } from "react-router-dom";
import { bookingOrdersAPI } from '../../api/apiClient';
import PaginationComponent from '../common/PaginationComponent';
import { Spinner, Alert, Button, Modal, Badge, Form, InputGroup, Col, Row, Card } from "react-bootstrap";
import Swal from 'sweetalert2';

const OrdersForBooking = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState([]);
  const [counts, setCounts] = useState({
    active: 0,
    pending: 0,
    assigned: 0,
    received: 0,
    completed: 0
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const { id } = useParams();
  
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  // Fetch booking orders based on tab with search
  const fetchBookingOrders = async (page = 1, search = searchTerm) => {
    try {
      setLoading(true);
      setError('');
      
      const params = {
        page,
        per_page: pagination.per_page,
        search,
      };

      const response = await bookingOrdersAPI.byBooking(id, params);
      
      if (response.success) {
        const fetchedData = response.data.data || response.data;
        setData(fetchedData);

        setPagination(response.data.meta || {
          current_page: page,
          last_page: response.data.last_page || 1,
          per_page: response.data.per_page || 10,
          total: response.data.total || 0,
        });
      } else {
        setError(response.message || t('failed_to_fetch_booking_orders'));
      }
    } catch (err) {
      setError(t('failed_to_fetch_booking_orders'));
      console.error('Error fetching booking orders:', err);
    } finally {
      setLoading(false);
    }
  };

  // Debounced Search Effect
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm !== undefined) {
        fetchBookingOrders(1, searchTerm);
      }
    }, 1500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);


  const handleAccept = async (id) => {
    try {
      setUpdating(true);
      const response = await bookingOrdersAPI.accept(id);
      
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("booking.order_accepted_successfully"),
          timer: 3000,
          showConfirmButton: false,
        });
        fetchBookingOrders();
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: err.message || t("failed_to_accept_order"),
        timer: 3000,
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleReject = async (id) => {
    if (!rejectReason.trim()) {
      Swal.fire({
        icon: "warning",
        title: t("warning"),
        text: t("booking.please_enter_reject_reason"),
        timer: 3000,
      });
      return;
    }

    try {
      setUpdating(true);
      const response = await bookingOrdersAPI.reject(id, { rejected_reason: rejectReason });
      
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("booking.rejected_successfully"),
          timer: 3000,
          showConfirmButton: false,
        });
        setShowRejectModal(false);
        setRejectReason('');
        fetchBookingOrders();
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: err.message || t("failed_to_reject"),
        timer: 3000,
      });
    } finally {
      setUpdating(false);
    }
  };

  useEffect(() => {
    fetchBookingOrders(1, searchTerm);
  }, [id]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchBookingOrders(1, searchTerm);
  };

  const handleTabChange = () => {
    setSearchTerm('');
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      const response = await bookingOrdersAPI.delete(deleteId);
      
      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: t('success'),
          text: t('booking_deleted_successfully'),
          timer: 3000,
          showConfirmButton: false
        });
        fetchBookingOrders(pagination.current_page);
      } else {
        setError(response.message || t('failed_to_delete_booking'));
      }
    } catch (err) {
      setError(t('failed_to_delete_booking'));
      console.error('Error deleting booking:', err);
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
      setDeleteId(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const handleViewDetails = (id) => {
    navigate(`/admin/booking-orders/details/${id}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getProgressPercentage = (order) => {
    if (!order) return 0;
    
    const statusOrder = ['pending', 'assigned', 'received', 'completed'];
    const currentStatusIndex = statusOrder.indexOf(order.status);
    return Math.round((currentStatusIndex / (statusOrder.length - 1)) * 100);
  };

  const getStatusBadge = (status) => {
      const statusConfig = {
        cancelled: { variant: 'danger', text: t('cancelled') },
        rejected: { variant: 'danger', text: t('rejected') },
        approved: { variant: 'success', text: t('approved') },
        assigned: { variant: 'info', text: t('assigned') },
        received: { variant: 'secondary', text: t('received') },
        completed: { variant: 'success', text: t('completed') },
        pending: { variant: 'warning', text: t('pending') }
      };
      
      const config = statusConfig[status] || { variant: 'light', text: status };
      return <Badge bg={config.variant}>{config.text}</Badge>;
    };

  if (loading && data.length === 0) {
    return (
      <div className="driver-page">
        <div className="text-center py-4">
          <Spinner animation="border" />
          <p className="mt-2">{t('loading')}</p>
        </div>
      </div>
    );
  }

  const clearSearch = () => {
    setSearchTerm('');
  };

  const handleBack = () => {
    navigate(`/admin/bookings/details/${id}`);
  };

  return (
    <div className="driver-page">
      <div className="top-bar">
        {/* Search Bar */}
        <Row className="mb-3">
          <Col md={6}>
            <InputGroup>
              <InputGroup.Text><FaSearch /></InputGroup.Text>
              <Form.Control
                type="text"
                placeholder={t('search_order_driver')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
              />
              {searchTerm && (
                <Button 
                  variant="outline-secondary" 
                  onClick={clearSearch}
                  title={t("reset_search")}
                >
                  ✕
                </Button>
              )}
            </InputGroup>
          </Col>
          <Col md={6} className='d-flex justify-content-end'>
            <Button variant="outline-secondary" onClick={handleBack} size="sm">
              <FaArrowLeft className="me-1" />
              {t("booking.back")}
            </Button>
          </Col>
        </Row>
        
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

       <div className="orders">
        {data.length === 0 ? (
          <div className="text-center py-5">
            <h4>{t('no_bookings_found')}</h4>
            {/* <p className="text-muted">{t('no_bookings_description')}</p> */}
          </div>
        ) : (
          data.map((order) => (
            <Card key={order.id} className="order-card mb-3 shadow-sm">
              <Card.Body>
                {/* Header Section */}
                <Row className="align-items-center mb-3">
                  <Col md={4}>
                    <div className="d-flex align-items-center">
                      <FaTruck className="text-primary me-2" />
                      <div>
                        <h6 className="mb-0 fw-bold">
                          {t('booking.order_id')}: 
                          {searchTerm ? (
                            <span 
                              dangerouslySetInnerHTML={{
                                __html: order.order_id?.replace(
                                  new RegExp(searchTerm, 'gi'), 
                                  match => `<mark class="bg-warning">${match}</mark>`
                                )
                              }} 
                            />
                          ) : (
                            order.order_id
                          )}
                        </h6>
                        <small className="text-muted">
                          {t('booking.booking_id')}: 
                          <Button
                            variant="link"
                            className="p-0 text-decoration-none"
                            onClick={() => navigate(`/admin/bookings/details/${order.booking?.id}`)}
                          >
                            {order.booking?.booking_id}
                          </Button>
                        </small>
                      </div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="text-center">
                      <h6 className="mb-0">
                          {order.driver_role === 'Driver' && order.customer_id ? (
                            <Button
                              variant="link"
                              className="p-0 ms-1 text-decoration-none"
                              onClick={() => navigate(`/admin/drivers/details/${order.customer_id}`)}
                            >
                              {order.driver_name || t('not_assigned')}
                            </Button>
                          ) : order.driver_role === 'Driver Company' && order.customer_id ? (
                            <Button
                              variant="link"
                              className="p-0 ms-1 text-decoration-none"
                              onClick={() => navigate(`/admin/drivers-company/details/${order.customer_id}`)}
                            >
                              {order.driver_name || t('not_assigned')}
                            </Button>
                          ) : (
                            <span className="ms-1">{searchTerm ? (
                            <span 
                              dangerouslySetInnerHTML={{
                                  __html: order.driver_name?.replace(
                                    new RegExp(searchTerm, 'gi'), 
                                    match => `<mark class="bg-warning">${match}</mark>`
                                  )
                                }} 
                              />
                              ) : (
                                order.driver_name
                              )}
                            </span>
                          )}
                      </h6>
                      <small className="text-muted">{order.driver_role}</small>
                    </div>
                  </Col>
                  <Col md={4} className="text-end">
                    {getStatusBadge(order.status)}
                    <div className="mt-1">
                      <Badge bg="light" text="dark">
                        {order.price} EUR
                      </Badge>
                    </div>
                  </Col>
                </Row>

                {/* Route Information */}
                <Row className="mb-3">
                  <Col md={5}>
                    <div className="route-info">
                      <div className="d-flex align-items-center mb-2">
                        <FaMapMarkerAlt className="text-success me-2" />
                        <small><strong>{t('from')}:</strong> {order.booking?.booking_from?.[0]}</small>
                      </div>
                      <div className="d-flex align-items-center">
                        <FaMapMarkerAlt className="text-danger me-2" />
                        <small><strong>{t('to')}:</strong> {order.booking?.booking_to?.[0]}</small>
                      </div>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="route-stats">
                      <div className="d-flex align-items-center mb-1">
                        <FaRoad className="text-info me-2" />
                        <small>{order.booking?.distance}</small>
                      </div>
                      <div className="d-flex align-items-center">
                        <FaWeightHanging className="text-warning me-2" />
                        <small>{order.booking?.total_weight}</small>
                      </div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="vehicle-info text-end">
                      <div className="d-flex align-items-center justify-content-end mb-1">
                        <FaTruck className="text-secondary me-2" />
                        <small>{order.vehicle_type}</small>
                      </div>
                      <small className="text-muted">{order.vehicle_number}</small>
                      <br />
                      <small className="text-muted">{order.phone_number}</small>
                    </div>
                  </Col>
                </Row>

                {/* Progress and Dates */}
                <Row className="align-items-center">
                  <Col md={6}>
                    <div className="progress-wrapper d-flex align-items-center">
                      <div className="progress-circle me-3">
                        <svg width="40" height="40" viewBox="0 0 36 36" className="circular-chart">
                          <path
                            className="circle-bg"
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          />
                          <path
                            className="circle"
                            strokeDasharray={`${getProgressPercentage(order)}, 100`}
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          />
                        </svg>
                      </div>
                      <div>
                        <div className="progress-percent fw-bold">
                          {getProgressPercentage(order)}%
                        </div>
                        <small className="text-muted">{t('progress')}</small>
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="dates-info text-end">
                      {order.assign_date && (
                        <div className="d-flex justify-content-end align-items-center mb-1">
                          <FaCalendarAlt className="text-muted me-2" />
                          <small>
                            <strong>{t('booking.assigned_on')}:</strong> {formatDate(order.assign_date)}
                          </small>
                        </div>
                      )}
                      {order.received_date && (
                        <div className="d-flex justify-content-end align-items-center mb-1">
                          <FaCalendarAlt className="text-primary me-2" />
                          <small>
                            <strong>{t('booking.received_on')}:</strong> {formatDate(order.received_date)}
                          </small>
                        </div>
                      )}
                      {order.completed_date && (
                        <div className="d-flex justify-content-end align-items-center">
                          <FaCalendarAlt className="text-success me-2" />
                          <small>
                            <strong>{t('booking.completed_on')}:</strong> {formatDate(order.completed_date)}
                          </small>
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>

                {/* Action Buttons */}
                <Row className="mt-3 pt-3 border-top">
                  <Col>
                    <div className="d-flex flex-wrap gap-2">
                      {order.status === 'pending' && (
                        <>
                          <Button 
                            size="sm"
                            variant="success"
                            onClick={() => handleAccept(order.id)}
                            disabled={updating}
                          >
                            <FaCheck className="me-1" /> {t("booking.accept")}
                          </Button>
                          <Button 
                            variant="danger" 
                            onClick={() => {
                              setDeleteId(order.id);
                              setShowRejectModal(true);
                            }}
                            disabled={updating}
                            size="sm"
                          >
                            <FaTimes className="me-1" /> {t("booking.reject")}
                          </Button>
                        </>
                      )}
                      <Button
                        variant="outline-info"
                        size="sm"
                        onClick={() => handleViewDetails(order.id)}
                      >
                        <FaInfoCircle /> {t("details")}
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDeleteClick(order.id)}
                      >
                        <FaTrashAlt /> {t("delete")}
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          ))
        )}
      </div>

      {pagination.total > 0 && (
        <PaginationComponent
          currentPage={pagination.current_page}
          lastPage={pagination.last_page}
          onPageChange={fetchBookingOrders}
          disabled={loading}
        />
      )}

      {/* Reject Modal */}
      <Modal show={showRejectModal} onHide={() => setShowRejectModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("booking.reject_booking")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>{t("booking.reject_reason")} *</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder={t("booking.enter_reject_reason")}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRejectModal(false)}>
            {t("cancel")}
          </Button>
          <Button variant="danger" onClick={() => handleReject(deleteId)} disabled={updating}>
            {updating ? <Spinner size="sm" /> : t("booking.confirm_reject")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('confirm_deletion')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t('confirm_delete_booking')}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleDeleteCancel}>
            {t('cancel')}
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm} disabled={loading}>
            {loading ? t('deleting') : t('delete')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OrdersForBooking;